import { useEffect, useMemo, useState } from 'react';
import { Control, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { orgCalendarDate } from '@/lib/utils/custom-date/custom-date';
import { SelectTriggerSkeleton } from '@/components/core';
import { DatePicker } from '@/components/core/date-picker/date-picker';
import { FormControl, FormField, FormItem, FormMessage } from '@/components/ui-kit/form';
import { FieldLabel } from '../required-fields';
import { Input } from '@/components/ui-kit/input';
import { Label } from '@/components/ui-kit/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui-kit/select';
import { cn } from '@/lib/utils';
import { GENDER_OPTIONS } from '@/constant/hr/genders';
import { SALUTATION_OPTIONS } from '@/constant/hr/salutations';
import {
  useGetBasicInfoCtcLevels,
  useGetBasicInfoDepartments,
  useSearchReportingManagers,
} from '../../../hooks/use-basic-info-lookups';
import { useGetDesignationsLookup } from '../../../hooks/use-employees';
import { BasicInfoValues } from '../../../schemas/basic-info-schema';
import { OverviewValues } from '../../../schemas/employee-form-schema';
import { UIPhoneInput } from '@/components/core/phone-input/phone-input';
import { LookupResult } from '../../../types/employee.types';
import { FIELD_GRID, FormSection, HISTORY_YEARS } from '../form-layout';
import { ProfilePhotoUpload } from '../basic-info/profile-photo-upload';
import { SearchableSelect } from '@/components/core/searchable-select/searchable-select';

/** The add form carries the business email / phone on top of the basic info. */
type BasicFormValues = OverviewValues & { Email?: string; PhoneNumber?: string };

export interface BasicInfoStepProps {
  isEditMode: boolean;
  /** `create`: only the minimum asked when adding a person (name, business email / phone and where they sit). */
  variant?: 'full' | 'create';
  /**
   * The employee editing their own profile: the fields HR owns (joining date, unit, reporting
   * manager, designation, employee id) are read-only and the CTC level is not shown at all.
   */
  isSelfService?: boolean;
  /** IAM user id of the person edited; left out of the reporting managers, since nobody reports to themselves. */
  employeeUserId?: string;
}

/** Legacy showed this example format as the employee id placeholder (not a translated string). */
const EMPLOYEE_ID_PLACEHOLDER = 'Ex. SLS-YYYYMMDD-XXX';

/** Legacy debounced the reporting-manager typeahead by 400 ms. */
const MANAGER_SEARCH_DEBOUNCE_MS = 400;

/** Not selectable; shown in a select that has nothing to offer (legacy showed an "N/A" department). */
const NO_OPTIONS_VALUE = '__no_options__';

type StringFieldName = {
  [K in keyof BasicInfoValues]: BasicInfoValues[K] extends string ? K : never;
}[keyof BasicInfoValues];

interface TextFieldProps {
  control: Control<BasicFormValues>;
  name: StringFieldName;
  labelKey: string;
  placeholderKey?: string;
  placeholder?: string;
  type?: 'text' | 'date';
  max?: string;
  disabled?: boolean;
  className?: string;
}

const TextField = ({
  control,
  name,
  labelKey,
  placeholderKey,
  placeholder,
  type = 'text',
  max,
  disabled,
  className,
}: TextFieldProps) => {
  const { t } = useTranslation();
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className={className}>
          <FieldLabel>{t(labelKey)}</FieldLabel>
          <FormControl>
            {type === 'date' ? (
              <DatePicker
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                name={field.name}
                ref={field.ref}
                max={max}
                yearsBack={HISTORY_YEARS}
                disabled={disabled}
              />
            ) : (
              <Input
                {...field}
                type={type}
                max={max}
                placeholder={placeholderKey ? t(placeholderKey) : placeholder}
                disabled={disabled}
              />
            )}
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};

/** Keeps the saved value selectable while the option list it belongs to is still loading. */
const withCurrentOption = (
  options: { value: string; label: string; key?: string }[],
  value: string,
  label: string,
  key?: string
) =>
  value && !options.some((option) => option.value === value)
    ? [...options, { value, label: label || value, key }]
    : options;

const useDebouncedValue = <T,>(value: T, delayMs: number): T => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timeout = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timeout);
  }, [value, delayMs]);
  return debounced;
};

export const BasicInfoStep = ({
  isEditMode,
  variant = 'full',
  isSelfService = false,
  employeeUserId,
}: BasicInfoStepProps) => {
  const { t } = useTranslation();
  const { control, setValue, watch } = useFormContext<BasicFormValues>();

  const departmentId = watch('DepartmentId');
  const departmentName = watch('DepartmentName');
  const departmentKey = watch('DepartmentKey');
  const designationName = watch('Designation');
  const reportingManagerName = watch('ReportingManagerName');
  const ctcLevelId = watch('CtcLevelId');

  const { data: departments = [], isLoading: isDepartmentsLoading } = useGetBasicInfoDepartments();
  const departmentOptions = withCurrentOption(
    departments.map((department) => ({
      value: department.ItemId,
      label: department.Title,
      key: department.Key ?? '',
    })),
    departmentId,
    departmentName,
    departmentKey
  );

  // The organization stands for the country of employment, so its CTC levels are the ones offered.
  const { data: ctcLevels = [], isLoading: isCtcLevelsLoading } = useGetBasicInfoCtcLevels();
  const selectedCtcLevel = ctcLevels.find((level) => level.ItemId === ctcLevelId);

  const { data: designationsData, isLoading: isDesignationsLoading } = useGetDesignationsLookup();
  const designationOptions = useMemo(
    () =>
      ((designationsData as { Designations?: LookupResult } | undefined)?.Designations?.items ?? [])
        .map((designation) => ({ value: designation.ItemId, label: designation.Title }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [designationsData]
  );

  const [managerKeyword, setManagerKeyword] = useState('');
  const debouncedManagerKeyword = useDebouncedValue(managerKeyword, MANAGER_SEARCH_DEBOUNCE_MS);
  const { data: managers = [], isLoading: isManagersLoading } = useSearchReportingManagers(
    debouncedManagerKeyword.trim()
  );
  const managerOptions = managers
    .filter((manager) => manager.UserId !== employeeUserId)
    .map((manager) => ({
      value: manager.UserId,
      label: manager.DisplayName,
      description: manager.Email,
    }));

  const today = orgCalendarDate(new Date().toISOString());

  const selectField = (
    name: 'Salutation' | 'Sex',
    labelKey: string,
    placeholderKey: string,
    options: { value: string; label: string }[]
  ) => (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FieldLabel>{t(labelKey)}</FieldLabel>
          <Select onValueChange={field.onChange} value={field.value}>
            <FormControl>
              <SelectTrigger>
                <SelectValue placeholder={t(placeholderKey)} />
              </SelectTrigger>
            </FormControl>
            <SelectContent>
              {options.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <FormMessage />
        </FormItem>
      )}
    />
  );

  const joiningDateField = (
    <TextField
      control={control}
      name="JoiningDate"
      labelKey="JOINING_DATE"
      type="date"
      disabled={isSelfService}
    />
  );

  const departmentField = (
    <FormField
      control={control}
      name="DepartmentId"
      render={({ field }) => (
        <FormItem>
          <FieldLabel>{t('DEPARTMENT')}</FieldLabel>
          {/* A saved department shows no name until the list loads, and an empty list says "no data". */}
          {isDepartmentsLoading ? (
            <SelectTriggerSkeleton />
          ) : (
            <Select
              onValueChange={(id) => {
                field.onChange(id);
                const department = departmentOptions.find((option) => option.value === id);
                setValue('DepartmentName', department?.label ?? '');
                setValue('DepartmentKey', department?.key ?? '');
              }}
              value={field.value}
              disabled={isSelfService}
            >
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder={t('SELECT_DEPARTMENT')} />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {departmentOptions.length === 0 && (
                  <SelectItem value={NO_OPTIONS_VALUE} disabled>
                    {t('NO_DATA_FOUND')}
                  </SelectItem>
                )}
                {departmentOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <FormMessage />
        </FormItem>
      )}
    />
  );

  const designationField = (
    <FormField
      control={control}
      name="DesignationId"
      render={({ field }) => (
        <FormItem>
          <FieldLabel>{t('DESIGNATION')}</FieldLabel>
          <FormControl>
            <SearchableSelect
              value={field.value}
              options={designationOptions}
              selectedLabel={designationName}
              onChange={(id, option) => {
                field.onChange(id);
                setValue('Designation', option.label);
              }}
              disabled={isSelfService}
              placeholder={t('SELECT_DESIGNATION')}
              searchPlaceholder={t('SEARCH')}
              emptyText={t('NO_RESULTS_FOUND')}
              isLoading={isDesignationsLoading}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );

  const employeeIdField = (
    <TextField
      control={control}
      name="EmployeeID"
      labelKey="EMPLOYEE_ID"
      placeholder={EMPLOYEE_ID_PLACEHOLDER}
      disabled={isEditMode || isSelfService}
    />
  );

  if (variant === 'create') {
    return (
      <div className="flex flex-col gap-8">
        <FormSection title={t('PERSONAL_DETAILS')}>
          <div className={FIELD_GRID}>
            <TextField
              control={control}
              name="FirstName"
              labelKey="FIRST_NAME"
              placeholderKey="TYPE_NAME"
            />
            <TextField
              control={control}
              name="LastName"
              labelKey="LAST_NAME"
              placeholderKey="TYPE_NAME"
            />
            <FormField
              control={control}
              name="Email"
              render={({ field }) => (
                <FormItem>
                  <FieldLabel>{t('EMAIL')}</FieldLabel>
                  <FormControl>
                    <Input
                      {...field}
                      value={field.value ?? ''}
                      type="email"
                      placeholder={t('TYPE_EMAIL')}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name="PhoneNumber"
              render={({ field }) => (
                <FormItem>
                  <FieldLabel>{t('TELEPHONE_NO')}</FieldLabel>
                  <FormControl>
                    <UIPhoneInput
                      {...field}
                      value={field.value ?? ''}
                      onChange={(value?: string) => field.onChange(value ?? '')}
                      placeholder={t('TELEPHONE_NO')}
                      international
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </FormSection>

        <FormSection title={t('PROFESSIONAL_INFORMATION')}>
          <div className={FIELD_GRID}>
            {joiningDateField}
            {departmentField}
            {designationField}
            {employeeIdField}
          </div>
        </FormSection>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-8">
      <FormSection title={t('PERSONAL_DETAILS')}>
        <div className="flex flex-col items-center gap-6 lg:flex-row lg:items-start">
          <FormField
            control={control}
            name="ProfileImageId"
            render={({ field }) => (
              <ProfilePhotoUpload fileId={field.value} onChange={field.onChange} />
            )}
          />

          <div className={cn(FIELD_GRID, 'w-full min-w-0 lg:flex-1')}>
            {selectField(
              'Salutation',
              'SALUTATION',
              'SELECT_SALUTATION',
              SALUTATION_OPTIONS.map((option) => ({
                value: option.value,
                label: t(option.labelKey),
              }))
            )}
            <TextField
              control={control}
              name="FirstName"
              labelKey="FIRST_NAME"
              placeholderKey="TYPE_NAME"
            />
            <TextField
              control={control}
              name="LastName"
              labelKey="LAST_NAME"
              placeholderKey="TYPE_NAME"
            />

            {selectField(
              'Sex',
              'GENDER',
              'SELECT_GENDER',
              GENDER_OPTIONS.map((option) => ({ value: option.value, label: t(option.labelKey) }))
            )}
            <TextField
              control={control}
              name="DateOfBirth"
              labelKey="DATE_OF_BIRTH_CERTIFICATE"
              type="date"
              max={today}
            />
            <TextField
              control={control}
              name="DateOfBirthOriginal"
              labelKey="DATE_OF_BIRTH_ORIGINAL"
              type="date"
              max={today}
            />
          </div>
        </div>
      </FormSection>

      <FormSection title={t('PROFESSIONAL_INFORMATION')}>
        <div className={FIELD_GRID}>
          {joiningDateField}

          {departmentField}

          <FormField
            control={control}
            name="ReportingManagerUserId"
            render={({ field }) => (
              <FormItem>
                <FieldLabel>{t('REPORTING_MANAGER')}</FieldLabel>
                <FormControl>
                  <SearchableSelect
                    value={field.value}
                    options={managerOptions}
                    selectedLabel={reportingManagerName}
                    onChange={(id, option) => {
                      field.onChange(id);
                      setValue('ReportingManagerName', option.label);
                    }}
                    onSearchChange={setManagerKeyword}
                    disabled={isSelfService}
                    placeholder={t('SEARCH_BY_NAME_OR_EMAIL')}
                    searchPlaceholder={t('SEARCH_BY_NAME_OR_EMAIL')}
                    emptyText={t('NO_RESULTS_FOUND')}
                    isLoading={isManagersLoading}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {designationField}

          {employeeIdField}

          {!isSelfService && (
            <>
              <FormField
                control={control}
                name="CtcLevelId"
                render={({ field }) => (
                  <FormItem>
                    <FieldLabel>{t('CTC_LEVEL')}</FieldLabel>
                    {isCtcLevelsLoading ? (
                      <SelectTriggerSkeleton />
                    ) : (
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {ctcLevels.map((level) => (
                            <SelectItem key={level.ItemId} value={level.ItemId}>
                              {level.Level}
                              {level.Ranks ? ` (${level.Ranks})` : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Derived from the selected CTC level and never persisted (legacy `CTCAmount`). */}
              <div className="space-y-2">
                <Label>{t('CTC_AMOUNT')}</Label>
                {isCtcLevelsLoading ? (
                  <SelectTriggerSkeleton />
                ) : (
                  <Input readOnly value={selectedCtcLevel?.CTC ?? ''} />
                )}
              </div>
            </>
          )}
        </div>
      </FormSection>
    </div>
  );
};
